import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  diagnoseFolder,
  planFolderUpload,
  selectSafeFiles,
  type FolderDiagnosis,
  type FolderUploadSummary,
  type SelectedFile,
} from "../../lib/folder";
import { inspectAIIntake, type AIIntakeReport } from "../../lib/ai-intake";
import { changeAvailability, publishFiles, type PublishResult } from "../../lib/publisher";
import { PreflightResult } from "../preflight/PreflightResult";
import { FolderPicker } from "../upload/FolderPicker";
import { ManifestReview } from "../upload/ManifestReview";
import { AISetupCard } from "../upload/AISetupCard";
import { BuildProgress } from "./BuildProgress";
import { LiveAppCard } from "./LiveAppCard";
import { posthog } from "../../lib/posthog";
import { listWorkspaces, type WorkspaceSummary } from "../../lib/account-client";
import type { WorkspaceCapabilityDecision, WorkspaceCapabilityKey } from "@mph/launch-control";
import { ProjectApiBuilder, type ProjectApiBuilderApi } from "../workspace/ProjectApiBuilder";
import { ApiDiscoveryReview, type ApiDiscoveryReviewApi } from "../upload/ApiDiscoveryReview";
import type { ProjectFlowDraft } from "../../lib/pending-publish";

type Stage = "choose" | "checking" | "review" | "publishing" | "connections" | "api-discovery" | "live";

const STAGE_COPY: Record<Exclude<Stage, "choose" | "connections" | "api-discovery" | "live">, { label: string; heading: string }> = {
  checking: { label: "Checking", heading: "Checking your app" },
  review: { label: "Safety check", heading: "Here’s what we checked" },
  publishing: { label: "Publishing", heading: "Opening your doorway" },
};

const NO_AI_REPORT: AIIntakeReport = {
  status: "none",
  providers: [],
  sanitizedPaths: [],
  blockedPaths: [],
};

const DEFAULT_WORKSPACE_API = { listWorkspaces };
const EMPTY_UPLOAD_SUMMARY: FolderUploadSummary = {
  totalBytes: 0,
  ignoredBytes: 0,
  uploadBytes: 0,
  largeFolders: [],
  oversizedFiles: [],
  blocked: false,
};

export function ProjectFlow({ initialWorkspaces, initialDraft, renderReviewGate, workspaceApi = DEFAULT_WORKSPACE_API, apiBuilderApi, apiDiscoveryApi }: {
  initialWorkspaces?: WorkspaceSummary[];
  initialDraft?: ProjectFlowDraft;
  renderReviewGate?: (draft: ProjectFlowDraft) => ReactNode;
  workspaceApi?: { listWorkspaces(): Promise<WorkspaceSummary[]> };
  apiBuilderApi?: ProjectApiBuilderApi;
  apiDiscoveryApi?: ApiDiscoveryReviewApi;
} = {}) {
  const [stage, setStage] = useState<Stage>(initialDraft ? "review" : "choose");
  const [files, setFiles] = useState<SelectedFile[]>(initialDraft?.files ?? []);
  const [omitted, setOmitted] = useState<string[]>(initialDraft?.omitted ?? []);
  const [uploadSummary, setUploadSummary] = useState<FolderUploadSummary>(initialDraft?.uploadSummary ?? EMPTY_UPLOAD_SUMMARY);
  const [diagnosis, setDiagnosis] = useState<FolderDiagnosis | null>(initialDraft?.diagnosis ?? null);
  const [aiReport, setAIReport] = useState<AIIntakeReport>(initialDraft?.aiReport ?? NO_AI_REPORT);
  const [liveUrl, setLiveUrl] = useState("");
  const [projectId, setProjectId] = useState("");
  const [apiDiscovery, setApiDiscovery] = useState<PublishResult["apiDiscovery"]>();
  const [availability, setAvailability] = useState<"live" | "offline">("live");
  const [error, setError] = useState("");
  const [visibility, setVisibility] = useState<"public" | "password">(initialDraft?.visibility ?? "public");
  const [visitorPassword, setVisitorPassword] = useState("");
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[] | null>(() => (
    initialWorkspaces === undefined ? null : eligibleWorkspaces(initialWorkspaces)
  ));
  const [workspaceId, setWorkspaceId] = useState(() => {
    const eligible = initialWorkspaces === undefined ? [] : eligibleWorkspaces(initialWorkspaces);
    return eligible.length === 1 ? eligible[0]!.id : "";
  });
  const connectionsHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (stage === "connections") connectionsHeadingRef.current?.focus();
  }, [stage]);

  useEffect(() => {
    if (initialWorkspaces !== undefined) return;
    let active = true;
    void workspaceApi.listWorkspaces().then((items) => {
      if (!active) return;
      const eligible = eligibleWorkspaces(items);
      setWorkspaces(eligible);
      setWorkspaceId(eligible.length === 1 ? eligible[0]!.id : "");
    }).catch(() => {
      if (active) setWorkspaces([]);
    });
    return () => { active = false; };
  }, [initialWorkspaces, workspaceApi]);

  const workspaceRequired = useMemo(() => Boolean(!projectId && workspaces && workspaces.length > 1 && !workspaceId), [projectId, workspaceId, workspaces]);
  const selectedWorkspace = workspaces?.find((workspace) => workspace.id === workspaceId)
    ?? (workspaces?.length === 1 ? workspaces[0] : undefined);
  const sourceCapabilityKey: WorkspaceCapabilityKey | null = diagnosis?.kind === "source-static"
    ? "sourceBuilds"
    : diagnosis?.kind === "source-runtime"
      ? "managedRuntimes"
      : null;
  const sourceCapability = sourceCapabilityKey
    ? selectedWorkspace?.capabilities?.[sourceCapabilityKey] ?? TEMPORARILY_UNAVAILABLE
    : null;
  const sourceBuildBlocked = Boolean(sourceCapability && sourceCapability.state !== "enabled");

  async function chooseFolder(list: FileList | File[]) {
    setStage("checking");
    setError("");
    const selection = selectSafeFiles(list);
    const nextDiagnosis = await diagnoseFolder(selection.included);
    const initialPlan = planFolderUpload(selection, nextDiagnosis);
    try {
      const inspection = initialPlan.summary.blocked
        ? { report: NO_AI_REPORT, files: initialPlan.files }
        : await inspectAIIntake(Array.from(list), initialPlan.files);
      const finalPlan = inspection.report.status === "blocked"
        ? initialPlan
        : planFolderUpload(selection, nextDiagnosis, inspection.files);
      setFiles(finalPlan.files);
      setOmitted(finalPlan.omitted);
      setUploadSummary(finalPlan.summary);
      setDiagnosis(nextDiagnosis);
      setVisibility(nextDiagnosis.kind === "source-runtime" ? "password" : "public");
      setVisitorPassword("");
      setAIReport(inspection.report);
      posthog.capture("folder_chosen", {
        file_count: inspection.files.length,
        omitted_count: finalPlan.omitted.length,
        total_bytes: finalPlan.summary.totalBytes,
        ignored_bytes: finalPlan.summary.ignoredBytes,
        upload_bytes: finalPlan.summary.uploadBytes,
        upload_blocked: finalPlan.summary.blocked,
        folder_kind: nextDiagnosis.kind,
        ai_status: inspection.report.status,
      });
      setStage("review");
    } catch {
      posthog.capture("folder_check_failed");
      setError("We could not finish checking this folder. Choose it again to retry.");
      setStage("choose");
    }
  }

  async function publish() {
    if (!diagnosis || diagnosis.kind === "unsupported") return;
    setStage("publishing");
    setError("");
    try {
      const result = await publishFiles(files, {
        ...(projectId ? { projectId } : {}),
        ...(!projectId && workspaceId ? { workspaceId } : {}),
        visibility,
        ...(visibility === "password" ? { password: visitorPassword } : {}),
      });
      setVisitorPassword("");
      setProjectId(result.projectId);
      setLiveUrl(result.url);
      setAvailability("live");
      posthog.capture("app_published", {
        is_republish: Boolean(projectId),
        file_count: files.length,
      });
      setApiDiscovery(result.apiDiscovery);
      if (result.apiMode === "discovered") {
        if (
          !result.apiDiscovery
          || result.apiDiscovery.projectId !== result.projectId
          || result.apiDiscovery.revisionId !== result.revision
        ) throw new Error("Doorway did not return the exact API discovery review for this published revision.");
        setStage("api-discovery");
      }
      else if (result.apiMode === "portable_manifest" && result.customHttp) setStage("connections");
      else setStage("live");
    } catch (caught) {
      posthog.capture("app_publish_failed", {
        is_republish: Boolean(projectId),
      });
      setError(caught instanceof Error ? caught.message : "Publishing stopped before your live app changed. Try again.");
      setStage("review");
    }
  }

  async function updateAvailability(next: "live" | "offline") {
    setError("");
    try {
      await changeAvailability(next === "live" ? "reactivate" : "offline", projectId);
      setAvailability(next);
      posthog.capture(next === "live" ? "app_put_back_online" : "app_taken_offline");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That change did not finish. Try again.");
    }
  }

  if (stage === "live") {
    return (
      <>
        <LiveAppCard
          availability={availability}
          liveUrl={liveUrl}
          plan={selectedWorkspace?.plan}
          onAvailabilityChange={(next) => void updateAvailability(next)}
          onReplace={() => setStage("choose")}
        />
        {error && <p className="error-message" role="alert">{error}</p>}
      </>
    );
  }
  if (stage === "connections") {
    const targetWorkspaceId = workspaceId || workspaces?.[0]?.id || "";
    return (
      <section className="flow-card" aria-labelledby="runtime-connections-heading">
        <div className="step-label">App connections</div>
        <h2 ref={connectionsHeadingRef} id="runtime-connections-heading" tabIndex={-1}>Add credentials for this action set</h2>
        <p className="review-intro">
          Review the portable manifest and store each external service credential in Doorway before activating this revision.
        </p>
        {targetWorkspaceId ? (
          <ProjectApiBuilder
            workspaceId={targetWorkspaceId}
            projectId={projectId}
            onActivated={() => setStage("live")}
            {...(apiBuilderApi ? { api: apiBuilderApi } : {})}
          />
        ) : (
          <p className="error-message" role="alert">Doorway could not resolve this project&apos;s workspace.</p>
        )}
      </section>
    );
  }
  if (stage === "api-discovery") {
    const targetWorkspaceId = workspaceId || workspaces?.[0]?.id || "";
    return (
      <section className="flow-card" aria-label="Review discovered APIs">
        {targetWorkspaceId ? (
          <ApiDiscoveryReview
            workspaceId={targetWorkspaceId}
            projectId={projectId}
            {...(apiDiscovery?.revisionId ? { expectedRevisionId: apiDiscovery.revisionId } : {})}
            onActivated={() => setStage("live")}
            {...(apiDiscoveryApi ? { api: apiDiscoveryApi } : {})}
          />
        ) : (
          <p className="error-message" role="alert">Doorway could not resolve this project&apos;s workspace.</p>
        )}
      </section>
    );
  }
  if (stage === "review" && diagnosis && renderReviewGate) {
    return renderReviewGate({ files, omitted, uploadSummary, diagnosis, aiReport, visibility });
  }
  const copy = stage === "choose" ? null : STAGE_COPY[stage];

  return (
    <section
      className={`flow-card${stage === "choose" ? " flow-card-upload" : ""}`}
      aria-label={stage === "choose" ? "Upload your app" : undefined}
      aria-labelledby={stage === "choose" ? undefined : "flow-heading"}
    >
      {copy && (
        <>
          <div className="step-label">{copy.label}</div>
          <h2 id="flow-heading">{copy.heading}</h2>
        </>
      )}

      {workspaces && workspaces.length > 1 && (
        <label className="workspace-picker publish-workspace-picker">
          <span>Publish to workspace</span>
          <select value={workspaceId} onChange={(event) => setWorkspaceId(event.target.value)}>
            <option value="">Choose a workspace</option>
            {workspaces.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}
          </select>
        </label>
      )}

      {stage === "choose" && (
        <>
          <FolderPicker onChoose={(list) => void chooseFolder(list)} />
          {error && <p className="error-message" role="alert">{error}</p>}
        </>
      )}
      {stage === "checking" && <BuildProgress label="Finding the safe, simple setup…" />}
      {stage === "review" && diagnosis && (
        <div className="review-stack">
          <p className="review-intro">
            Before uploading anything, Doorway looks for a working website, private files like <code>.env</code>, and API keys. This keeps private information off the public internet.
          </p>
          <PreflightResult diagnosis={diagnosis} />
          <AISetupCard report={aiReport} omittedPaths={omitted} />
          {diagnosis.kind === "source-runtime" && diagnosis.connections?.length ? (
            <div className="runtime-connection-preview">
              <strong>Connections Doorway will ask for after the build</strong>
              <ul>{diagnosis.connections.map((connection) => <li key={connection}>{connection}</li>)}</ul>
            </div>
          ) : null}
          <ManifestReview summary={uploadSummary} includedCount={files.length} omitted={omitted} mode={diagnosis.kind === "source-static" || diagnosis.kind === "source-runtime" ? "build" : "publish"} />
          <fieldset className="visibility-options publish-visibility">
            <legend>Who can open this link?</legend>
            <label className={visibility === "public" ? "visibility-option selected" : "visibility-option"}>
              <input type="radio" name="publish-visibility" value="public" checked={visibility === "public"} disabled={diagnosis.kind === "source-runtime"} onChange={() => { setVisibility("public"); setVisitorPassword(""); }} />
              <span><strong>Anyone with the link</strong><small>Best for public tools and quick sharing.</small></span>
            </label>
            <label className={visibility === "password" ? "visibility-option selected" : "visibility-option"}>
              <input type="radio" name="publish-visibility" value="password" checked={visibility === "password"} onChange={() => setVisibility("password")} />
              <span><strong>Password protected</strong><small>Visitors enter the password you choose before the app loads.</small></span>
            </label>
          </fieldset>
          {diagnosis.kind === "source-runtime" ? <p className="demo-disclaimer">Managed apps require a Doorway visitor password before API credentials can be activated.</p> : null}
          {visibility === "password" && <label className="agent-password-field">Visitor password<input type="password" value={visitorPassword} minLength={8} maxLength={256} autoComplete="new-password" required onChange={(event) => setVisitorPassword(event.target.value)} /><small>Doorway stores a one-way verifier. The password never enters your app files.</small></label>}
          {error && <p className="error-message" role="alert">{error}</p>}
          {workspaceRequired && <p className="error-message">Choose which workspace should own this app before publishing.</p>}
          <div className="button-row">
            <button className="primary-button" disabled={uploadSummary.blocked || sourceBuildBlocked || workspaceRequired || diagnosis.kind === "unsupported" || aiReport.status === "blocked" || (visibility === "password" && (visitorPassword.length < 8 || visitorPassword.length > 256))} type="button" onClick={() => void publish()}>
              {sourceCapability && sourceCapability.state !== "enabled"
                ? sourceCapabilityButtonLabel(sourceCapability, diagnosis.kind)
                : diagnosis.kind === "source-runtime"
                  ? "Build app and add connections"
                  : diagnosis.kind === "source-static"
                    ? "Build and publish"
                    : selectedWorkspace?.plan === "solo" || selectedWorkspace?.plan === "team"
                      ? "Publish"
                      : "Publish for free"}
            </button>
            <button className="secondary-button" type="button" onClick={() => setStage("choose")}>Choose a different folder</button>
          </div>
          <p className="demo-disclaimer">{sourceCapability && sourceCapability.state !== "enabled"
            ? sourceCapabilityMessage(sourceCapability, diagnosis.kind)
            : diagnosis.kind === "source-runtime"
              ? "Doorway installs locked dependencies without lifecycle scripts, runs the server in an isolated Cloudflare Sandbox, and proxies only the reviewed API hosts."
              : diagnosis.kind === "source-static"
                ? "Sandbox builds install locked dependencies without lifecycle scripts, run inside a temporary isolated container, and delete the container when the build ends."
                : selectedWorkspace?.plan === "solo" || selectedWorkspace?.plan === "team"
                  ? "Doorway publishes finished website files only and never runs code from your folder."
                  : "Free includes one published app with a small Doorway badge. Doorway publishes finished website files only and never runs code from your folder."}</p>
        </div>
      )}
      {stage === "publishing" && <BuildProgress label={diagnosis?.kind === "source-runtime" ? "Building and starting the unchanged app…" : diagnosis?.kind === "source-static" ? "Building inside a private sandbox…" : "Checking and publishing safe files…"} />}
    </section>
  );
}

function eligibleWorkspaces(workspaces: WorkspaceSummary[]): WorkspaceSummary[] {
  return workspaces.filter((workspace) => workspace.role === "owner" || workspace.role === "creator");
}

const TEMPORARILY_UNAVAILABLE: WorkspaceCapabilityDecision = { state: "temporarily_unavailable" };

function sourceCapabilityButtonLabel(
  capability: WorkspaceCapabilityDecision,
  diagnosisKind: FolderDiagnosis["kind"],
): string {
  const subject = diagnosisKind === "source-runtime" ? "Managed runtimes" : "Source builds";
  switch (capability.state) {
    case "upgrade_required":
      return diagnosisKind === "source-runtime" ? "Upgrade to Solo for managed runtimes" : "Upgrade to Solo for source builds";
    case "permission_denied":
      return diagnosisKind === "source-runtime" ? "Managed runtime permission required" : "Source build permission required";
    case "access_paused":
      return `${subject} are paused`;
    case "temporarily_unavailable":
      return `${subject} are temporarily unavailable`;
    case "enabled":
      return diagnosisKind === "source-runtime" ? "Build app and add connections" : "Build and publish";
  }
}

function sourceCapabilityMessage(
  capability: WorkspaceCapabilityDecision,
  diagnosisKind: FolderDiagnosis["kind"],
): string {
  const runtime = diagnosisKind === "source-runtime";
  switch (capability.state) {
    case "upgrade_required":
      return runtime
        ? "Solo and Team include isolated managed runtimes for server-backed apps."
        : "Solo and Team include isolated source builds for projects with locked dependencies.";
    case "permission_denied":
      return runtime
        ? "Owner or Creator permission is required to start a managed runtime."
        : "Owner or Creator permission is required to run a source build.";
    case "access_paused":
      return runtime
        ? "Managed runtimes are paused while workspace access is paused."
        : "Source builds are paused while workspace access is paused.";
    case "temporarily_unavailable":
      return runtime
        ? "Managed runtime infrastructure is temporarily unavailable. Try again later."
        : "Source build infrastructure is temporarily unavailable. Try again later.";
    case "enabled":
      return runtime
        ? "Doorway installs locked dependencies without lifecycle scripts, runs the server in an isolated Cloudflare Sandbox, and proxies only the reviewed API hosts."
        : "Sandbox builds install locked dependencies without lifecycle scripts, run inside a temporary isolated container, and delete the container when the build ends.";
  }
}
